import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'
import { useLiveChats, useLiveChat, sendLiveChatMessage } from '@/hooks/useLiveChats'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Send, MessageCircle, Trash2 } from 'lucide-react'
import { useAuthState } from '@/hooks/useAuth'
import { getAdminDisplayName, getAdminProfilePicture } from '@/lib/liveChatUtils'
import { doc, getDoc, deleteDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'

export default function LiveChatAdminPage() {
  const { chatId } = useParams<{ chatId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthState()
  const [selectedChatUserId, setSelectedChatUserId] = useState<string | null>(chatId || null)
  const [messageInput, setMessageInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null)
  const [usernames, setUsernames] = useState<{ [userId: string]: string }>({})
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { liveChats, loading: listLoading } = useLiveChats()
  const { liveChat: selectedChat, loading: detailLoading } = useLiveChat(selectedChatUserId || undefined)
  
  // Update selectedChatUserId when chatId URL parameter changes
  useEffect(() => {
    setSelectedChatUserId(chatId || null)
  }, [chatId])

  const adminName = user?.email ? getAdminDisplayName(user.email) : 'Admin'

  // Fetch usernames for all chats
  useEffect(() => {
    const fetchUsernames = async () => {
      const newUsernames: { [userId: string]: string } = {}
      
      for (const chat of liveChats) {
        if (!usernames[chat.userId]) {
          try {
            const userDoc = await getDoc(doc(db, 'users', chat.userId))
            if (userDoc.exists()) {
              const userData = userDoc.data()
              newUsernames[chat.userId] = userData.instagram || userData.email || 'Unknown User'
            }
          } catch (error) {
            console.error('Error fetching username:', error)
          }
        }
      }
      
      if (Object.keys(newUsernames).length > 0) {
        setUsernames(prev => ({ ...prev, ...newUsernames }))
      }
    }

    fetchUsernames()
  }, [liveChats])

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [selectedChat?.messages])

  // Auto-grow textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = '44px'
    const newHeight = Math.min(textarea.scrollHeight, 84)
    textarea.style.height = `${newHeight}px`
  }

  useEffect(() => {
    adjustTextareaHeight()
  }, [messageInput])

  const handleSendMessage = async () => {
    if (!messageInput.trim() || isSending || !selectedChatUserId) return

    setIsSending(true)

    try {
      await sendLiveChatMessage(
        selectedChatUserId,
        messageInput.trim(),
        adminName,
        true
      )
      setMessageInput('')
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  const formatDateTime = (date: Date) => {
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleDeleteChat = async (chatUserId: string, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent navigating to the chat when clicking delete
    
    if (!confirm('Are you sure you want to delete this chat? This action cannot be undone.')) {
      return
    }

    setDeletingChatId(chatUserId)

    try {
      await deleteDoc(doc(db, 'liveChats', chatUserId))
    } catch (error) {
      console.error('Error deleting chat:', error)
      alert('Failed to delete chat. Please try again.')
    } finally {
      setDeletingChatId(null)
    }
  }

  const canSend = messageInput.trim() && !isSending

  // Chat Detail View
  if (selectedChatUserId && selectedChat) {
    return (
      <AdminAuthGuard>
        <div className="min-h-screen flex flex-col bg-background">
          {/* Header - matching ChatDetailPage style */}
          <div className="sticky top-0 z-50 bg-background border-border relative">
            <div className="border-b border-border">
              <div className="phone-container-padded py-4">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/live-chat-admin')}
                    className="h-10 w-10 rounded-full flex-shrink-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* User avatar */}
                    <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground flex-shrink-0 text-lg font-medium">
                      {(usernames[selectedChat.userId] || 'U')[0].toUpperCase()}
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <h1 className="text-base font-medium truncate">
                        {usernames[selectedChat.userId] || 'Loading...'}
                      </h1>
                      <p className="text-xs text-muted-foreground truncate">
                        Started {formatDateTime(selectedChat.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Admin Info Banner */}
            <div className="border-b border-border bg-muted/30">
              <div className="phone-container-padded py-3 px-4">
                <p className="text-sm text-muted-foreground text-center">
                  Replying as <strong className="text-foreground">{adminName}</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div 
            className="flex-1 overflow-y-auto"
            style={{ 
              paddingBottom: 'calc(44px + 8px + 2rem + 72px)' // input height + gap + padding + bottom nav
            }}
          >
            <div className="phone-container-padded py-4">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-sm text-muted-foreground">Loading messages...</p>
                  </div>
                </div>
              ) : selectedChat.messages && selectedChat.messages.length > 0 ? (
                <div className="space-y-2">
                  {selectedChat.messages.map((message, index) => {
                    const isOwnMessage = message.isAdmin // Admin's messages appear on the right
                    const showAvatar = !message.isAdmin && (
                      index === selectedChat.messages.length - 1 || 
                      selectedChat.messages[index + 1]?.isAdmin !== message.isAdmin
                    )
                    const showAdminAvatar = message.isAdmin && (
                      index === selectedChat.messages.length - 1 || 
                      selectedChat.messages[index + 1]?.isAdmin !== message.isAdmin
                    )
                    
                    return (
                      <div
                        key={index}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} w-full`}
                      >
                        <div className={`flex ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'} items-end gap-2 max-w-[85%]`}>
                          {/* Avatar */}
                          {!isOwnMessage && (
                            <div className="w-8 h-8 flex-shrink-0">
                              {showAvatar && (
                                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium">
                                  {(usernames[selectedChat.userId] || 'U')[0].toUpperCase()}
                                </div>
                              )}
                            </div>
                          )}
                          {isOwnMessage && (
                            <div className="w-8 h-8 flex-shrink-0">
                              {showAdminAvatar && (
                                <img
                                  src={getAdminProfilePicture(message.senderUsername)}
                                  alt={message.senderUsername}
                                  className="w-8 h-8 rounded-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(message.senderUsername)}&background=000&color=fff&size=128&length=1&bold=true`
                                  }}
                                />
                              )}
                            </div>
                          )}
                          
                          {/* Message bubble */}
                          <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                            {/* Sender name */}
                            {!isOwnMessage && showAvatar && (
                              <p className="text-xs font-medium text-muted-foreground mb-1 ml-1">
                                {usernames[selectedChat.userId] || 'User'}
                              </p>
                            )}
                            {isOwnMessage && showAdminAvatar && (
                              <p className="text-xs font-medium text-muted-foreground mb-1 mr-1">
                                {message.senderUsername}
                              </p>
                            )}
                            
                            <div 
                              className={`${
                                isOwnMessage
                                  ? 'bg-primary text-primary-foreground rounded-[18px] rounded-br-[4px]'
                                  : 'bg-muted text-foreground rounded-[18px] rounded-bl-[4px]'
                              } px-4 py-3 shadow-sm`}
                            >
                              <p className="text-[15px] leading-[20px] whitespace-pre-wrap break-words">
                                {message.content}
                              </p>
                            </div>
                            
                            {/* Timestamp */}
                            <p className={`text-[10px] text-muted-foreground mt-1 ${isOwnMessage ? 'mr-1' : 'ml-1'}`}>
                              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Send className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground text-center">
                    No messages yet in this conversation
                  </p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area - Sticky just above bottom nav */}
          <div className="sticky bottom-[72px] left-0 right-0 z-40 bg-background border-t border-border">
            <div className="phone-container-padded">
              <div 
                className="px-4 pt-2"
                style={{ paddingBottom: '2rem' }}
              >
                <div className="flex gap-3">
                  {/* Text Input */}
                  <div className="flex-1">
                    <textarea
                      ref={textareaRef}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      placeholder="Type your reply..."
                      disabled={isSending}
                      rows={1}
                      className="w-full min-h-[44px] max-h-[84px] px-4 py-3 bg-muted rounded-[var(--card-radius)] border-0 focus:outline-none resize-none text-[15px] leading-[20px] placeholder:text-muted-foreground overflow-y-auto disabled:opacity-50"
                      style={{
                        scrollbarWidth: 'thin',
                        msOverflowStyle: 'scrollbar'
                      }}
                    />
                  </div>

                  {/* Send Button */}
                  <div className="shrink-0">
                    <button
                      onClick={handleSendMessage}
                      disabled={!canSend}
                      className={`aspect-square flex items-center justify-center rounded-[var(--card-radius)] transition-all ${
                        canSend 
                          ? 'bg-primary hover:bg-primary/90 text-primary-foreground' 
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      }`}
                      style={{ height: '44px' }}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AdminAuthGuard>
    )
  }

  // Chat List View
  return (
    <AdminAuthGuard>
      <div className="min-h-screen flex flex-col bg-background">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-background border-border relative">
          <div className="border-b border-border">
            <div className="phone-container-padded py-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="h-10 w-10 rounded-full flex-shrink-0"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                
                <div className="min-w-0 flex-1">
                  <h1 className="text-base font-medium truncate">
                    Live Chat Admin
                  </h1>
                  <p className="text-xs text-muted-foreground truncate">
                    Manage conversations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat List */}
        <div 
          className="flex-1 overflow-y-auto"
          style={{ paddingBottom: '72px' }}
        >
          <div className="phone-container-padded py-4">
            {listLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-sm text-muted-foreground">Loading chats...</p>
                </div>
              </div>
            ) : liveChats.length > 0 ? (
              <div className="space-y-2">
                {liveChats.map((chat) => {
                  const lastMessage = chat.messages[chat.messages.length - 1]
                  const statusColor = chat.lastMessageIsFromUser ? 'bg-red-500' : 'bg-green-500'
                  
                  return (
                    <div
                      key={chat.id}
                      className="relative group"
                    >
                      <button
                        onClick={() => navigate(`/live-chat-admin/${chat.userId}`)}
                        className="w-full text-left p-4 bg-background border border-border rounded-[var(--card-radius)] hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {/* User avatar with status indicator */}
                          <div className="relative flex-shrink-0">
                            <div className="h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-lg font-medium">
                              {(usernames[chat.userId] || 'U')[0].toUpperCase()}
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full ${statusColor} border-2 border-white`} />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="font-medium truncate">
                                {usernames[chat.userId] || 'Loading...'}
                              </p>
                              <p className="text-xs text-muted-foreground flex-shrink-0">
                                {formatDateTime(chat.lastMessageAt)}
                              </p>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {lastMessage?.isAdmin && <span className="font-medium">{lastMessage.senderUsername}: </span>}
                              {lastMessage?.content || 'No messages yet'}
                            </p>
                          </div>
                        </div>
                      </button>
                      
                      {/* Delete button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDeleteChat(chat.userId, e)}
                        disabled={deletingChatId === chat.userId}
                        className="absolute top-2 right-2 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full"
                        title="Delete chat"
                      >
                        {deletingChatId === chat.userId ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <MessageCircle className="w-7 h-7 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground font-medium">No conversations yet</p>
                <p className="text-sm text-muted-foreground mt-1 text-center">
                  New chats will appear here when users message
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminAuthGuard>
  )
}

